import Typography from '@alltrails/core/components/Typography';
import Heading from '@alltrails/denali/components/Heading';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import Caution from '@alltrails/denali/icons/Caution';
import PageUISectionList, { type PageUISectionComponents } from '@alltrails/layout/components/PageUI/PageUISectionList/PageUISectionList';
import AskFeedback from '@/components/AskSidebar/AskFeedback';
import type useAskTurn from '@/hooks/useAskTurn';
import type { AskAllTrailsRequest } from '@/utils/askAllTrailsApi';
import styles from './AskSidebar.module.scss';

// Ask reads like search: insights sit right under their trail's card, so the lines between trails go
const sectionComponents: PageUISectionComponents = { divider: () => null };

type Props = Omit<ReturnType<typeof useAskTurn>, 'request'> & {
  isChat?: boolean;
  request: AskAllTrailsRequest;
};

/**
 * The submitted question, as the title or as the user's message in a chat, then the answer, or where the request is up to.
 */
const AskResponse = ({ error, isChat, isFetching, page, request, requestId }: Props) => {
  const loadingLabel = 'Searching trails and reviews…'; // hwandrew TODO: XPLR-204 translations
  const timeoutErrorMessage = 'We’re having trouble finding trails. Please try again.'; // hwandrew TODO: XPLR-204 translations
  const genericErrorMessage = 'There’s an issue on our side. Please try again.'; // hwandrew TODO: XPLR-204 translations

  return (
    <div className={styles.response}>
      {isChat ? (
        <Typography className={styles.chatQuestion} component="h3" mb="0" mt="0" variant="text200">
          {request.query}
        </Typography>
      ) : (
        <Heading as="h3" className={styles.question} size="300">
          {request.query}
        </Heading>
      )}
      {isFetching && (
        <div className={styles.loading} role="status">
          <LoadingDots />
          <Typography component="span" variant="text200">
            {loadingLabel}
          </Typography>
        </div>
      )}
      {error && (
        <div className={styles.error} role="alert">
          <Caution className={styles.errorIcon} color="--color-text-error" size="md" />
          <Typography color="error" component="p" variant="text100">
            {error.type === 'timeout' ? timeoutErrorMessage : genericErrorMessage}
          </Typography>
        </div>
      )}
      {page && (
        <>
          <PageUISectionList components={sectionComponents} insightsInTrailResults page={page} />
          <AskFeedback isChat={isChat} key={requestId} requestId={requestId} />
        </>
      )}
    </div>
  );
};

export default AskResponse;
